// DEPRECATED - use channel specific files
import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  bool, func, oneOf, oneOfType, shape, string
} from 'prop-types';
import axios from 'axios';
import { useDataModel, useLazyDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';
import { useLifeCycleEventBus } from '@thd-olt-functional/utils';
import { useDecisionEngine } from '../hooks/useDecisionEngine';
import Helper from './Helper';
import './media-gallery.style.scss';
import { componentReady } from '../publisher';
import { MediaGalleryDesktop, MediaGalleryMobile } from './subcomponents';
import { MediaHighlight } from './subcomponents/MediaHighlight/MediaHighlight';
import { dataModel } from './dataModel';
import { MediaGalleryLoader } from './subcomponents/MediaGalleryLoader/MediaGalleryLoader';

export const MediaGallery = ({
  collectionId,
  hideProductImagesLabel,
  highlight,
  highRes,
  hover,
  itemId,
  configId,
  marqueeRef,
  media,
  onMediaClick,
  showFavorites,
  showLoader,
  firstItemFetchPriority
}) => {
  const { externalMedia } = useLifeCycleEventBus('configurator.configurator_media_gallery_event');
  const mediaMapRef = useRef(null);
  const [highResImages, setHighResImages] = useState([]);
  const experienceContext = useContext(ExperienceContext);
  const { channel } = experienceContext;
  const isMobile = channel === 'mobile';
  const storeId = useStoreId();
  const [superSkuHoverId, setSuperSkuHoverId] = useState('');
  useEffect(() => {
    if (hover) {
      window.LIFE_CYCLE_EVENT_BUS.on('supersku.hover', (hoverItemId) => {
        setSuperSkuHoverId(hoverItemId?.output);
      });
    }
  }, [hover]);

  const [superSkuHoveredProductRequest, superSkuHoveredProductResponse] = useLazyDataModel('product', { });

  useEffect(() => {
    if (superSkuHoverId) {
      superSkuHoveredProductRequest({
        variables: {
          itemId: superSkuHoverId,
          configId,
          storeId,
          skipPaintDetails: !configId
        }
      });
    }
  }, [superSkuHoverId]);

  useEffect(() => {
    if (itemId) {
      componentReady({ itemId });
    }
  }, [itemId]);

  const productData = useDataModel('product', { variables: {
    itemId,
    configId,
    storeId,
    skipPaintDetails: !configId
  },
  skip: !itemId });
  const collectionData = useDataModel('collection', { variables: { collectionId, storeId }, skip: !collectionId });

  const { product } = productData?.data || {};
  const { collection } = collectionData?.data || {};
  const { metadata } = collection || {};

  const decisionEngineResponse = useDecisionEngine({ media: product?.media });

  // for swatch Hover
  const mainSuperSkuHover = Helper.getMainSuperSkuHover({
    itemId, superSkuHoveredProductResponse, collectionData, overrideData: { media }, externalMedia
  });
  const { displayedMedia, ...apiResults } = Helper.getDisplayedMedia({
    decisionEngineResponse,
    isMobile,
    itemId,
    productData,
    collectionId,
    collectionData,
    externalMedia,
    overrideData: { media },
    placeholder: showLoader && <MediaGalleryLoader isMobile={isMobile} />
  });
  const { zoom } = displayedMedia;

  useEffect(() => {
    // TODO: Hook
    if (channel !== 'mobile' && highRes && zoom) {
      const checkValidityPromise = [];
      zoom.forEach((zoomedImage) => {
        const imageCheckPromise = Helper.getHighResImages(zoomedImage, axios);
        checkValidityPromise.push(imageCheckPromise);
      });
      Promise.all(checkValidityPromise).then((responses) => {
        const images = responses.filter((resp) => !!resp);
        if (images.length) {
          setHighResImages(images);
        }
      }).catch(() => { });
    }
  }, [itemId, !!zoom]);

  if (apiResults.apiState) {
    return apiResults.returnStatement;
  }

  if (highlight) {
    return <MediaHighlight itemId={itemId} />;
  }

  const handleMediaClick = ({ media: clickedMedia }) => {
    if (onMediaClick) {
      onMediaClick({ media: clickedMedia });
    }
  };

  mediaMapRef.current = displayedMedia;

  if (highResImages) {
    mediaMapRef.current = {
      ...mediaMapRef.current,
      highResolution: highResImages
    };
  }

  return (
    <div>
      {isMobile ? (
        <MediaGalleryMobile
          media={mediaMapRef.current}
          onMediaClick={handleMediaClick}
          product={product || metadata}
          showFavorites={showFavorites}
          firstItemFetchPriority={firstItemFetchPriority}
          itemId={itemId}
          showLoadingPlaceholder={decisionEngineResponse?.loading}
          imageType={decisionEngineResponse?.imageType}
        />
      ) : (
        <MediaGalleryDesktop
          media={mediaMapRef.current}
          collection={collection}
          product={product}
          totalThumbnailsToShow={6}
          hideProductImagesLabel={hideProductImagesLabel}
          mainSuperSkuHover={mainSuperSkuHover}
          showFavorites={showFavorites}
          marqueeRef={marqueeRef}
          itemId={itemId}
          configId={configId}
          mainImageFetchPriority={firstItemFetchPriority}
          showLoadingPlaceholder={decisionEngineResponse?.loading}
          imageType={decisionEngineResponse?.imageType}
        />
      )}
    </div>
  );
};

MediaGallery.displayName = 'MediaGallery';

MediaGallery.propTypes = {
  collectionId: string,
  hideProductImagesLabel: bool,
  highlight: bool,
  highRes: bool,
  hover: bool,
  itemId: string,
  configId: string,
  onMediaClick: func,
  marqueeRef: oneOfType([
    func,
    shape({})
  ]),
  media: shape({}),
  showLoader: bool,
  showFavorites: bool,
  firstItemFetchPriority: oneOf([
    'high', // Fetch the image at a high priority relative to other images.
    'low', // Fetch the image at a low priority relative to other images.
    'auto' // Indicates no preference for the fetch priority. The browser decides what is best for the user.
  ])
};

MediaGallery.defaultProps = {
  collectionId: null,
  hideProductImagesLabel: false,
  highlight: false,
  highRes: false,
  hover: false,
  itemId: null,
  configId: undefined,
  onMediaClick: null,
  marqueeRef: null,
  media: null,
  showLoader: false,
  showFavorites: false,
  firstItemFetchPriority: 'auto'
};

MediaGallery.dataModel = dataModel;
